/* eslint-disable @typescript-eslint/no-unused-vars */
import { openDB } from "idb";
import type { NetworkEvent } from "../../lib/Events";
import {
	type DbRecording, type DbRecordedEvent, type DbVideoFragment, type DbMhtmlCapture, DbExtensionSettings,
	// NameOfEventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex,
	NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex,
	NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName,
	NameOfIndexForReceivedEventUuidThenEventName,
	NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode,
	NameOfIndexForRecordingUuidThenMhtmlCaptureUuid, DbHarEntry,
	NameOfHarEntryTableIndexForRecordingUuidStartTime,
	NameOfIndexForEventListenerUuidThenEventName, DbHarLog, DbErrorLogEntry,
	NameOfErrorLogTableIndexForRecordingUuidThenErrorTime, DbTabBeingRecorded,
	NameOfTabBeingRecordedTableIndexForRecordingUuid,
	NameOfRecordingsTableIndexForStatus,
	ISODateTimeString
} from "./DatabaseTypes";
import type { EvidenceDatabase, EvidenceDatabaseSchema } from "./EvidenceDbStruct";
import { ProcessInfo } from "../ProcessInfo";
import { InstrumentationEventDomEventReceived } from "../../lib/Events/InstrumentationEvent";
import { DbSignedLicense } from "./License";

const dbVersion = 5;

const createDatabase = async () => {
	try {
		ProcessInfo.log("Attempting to open database");
		const db = await openDB<EvidenceDatabaseSchema>("Evidence", dbVersion, {
			upgrade(db, oldVersion, newVersion, transaction) {
				console.log(`Upgrading Evidence database from version ${oldVersion} to ${newVersion}`);

				if (oldVersion < 1) {
					const recordingsStore = db.createObjectStore("recordings", {
						keyPath: "recordingUuid" satisfies keyof DbRecording,
					});
					recordingsStore.createIndex(NameOfRecordingsTableIndexForStatus,
						"status" satisfies keyof DbRecording
					);
					const tabIdToRecordingUuidStore = db.createObjectStore("tabsBeingRecorded", {
						keyPath: "tabId" satisfies (keyof DbTabBeingRecorded)
					});
					tabIdToRecordingUuidStore.createIndex(NameOfTabBeingRecordedTableIndexForRecordingUuid, "recordingUuid" satisfies keyof DbTabBeingRecorded);
					const harLogStore = db.createObjectStore("harLogs", { keyPath: ["recordingUuid", "whenRecordedIso"] as (keyof DbHarLog)[] });
					const errorStore = db.createObjectStore("errors", {
						keyPath: ["recordingUuid", "errorUuid"] satisfies (keyof DbErrorLogEntry)[]
					})
					errorStore.createIndex(NameOfErrorLogTableIndexForRecordingUuidThenErrorTime,
						["recordingUuid", "errorTimeIso"] satisfies [keyof DbErrorLogEntry, keyof DbErrorLogEntry]
					);
					const harEntryStore = db.createObjectStore("harEntries", {
						keyPath: ["recordingUuid", "entryUuid"] satisfies (keyof DbHarEntry)[],
					});
					harEntryStore.createIndex(NameOfHarEntryTableIndexForRecordingUuidStartTime,
						["recordingUuid", "startedDateTime"] satisfies [keyof DbHarEntry, keyof DbHarEntry]
					);
					const settingsStore = db.createObjectStore("extensionSettings", {
						keyPath: "settingsIndex" satisfies keyof DbExtensionSettings,
						autoIncrement: true,
					});
					const eventsStore = db.createObjectStore("events", {
							keyPath: ["recordingUuid", "eventIndex"] satisfies (keyof DbRecordedEvent)[]
					});
					eventsStore.createIndex(NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName,
						["recordingUuid", "raw.requestId", "category", "eventName"] satisfies [keyof DbRecordedEvent, `${keyof NetworkEvent}.${NetworkEvent["raw"]["requestId"]}`, keyof DbRecordedEvent, keyof DbRecordedEvent]);
					eventsStore.createIndex(NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex,
						["recordingUuid", "category", "eventName", "eventIndex"] satisfies [keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent]);
					eventsStore.createIndex(NameOfIndexForEventListenerUuidThenEventName,
						["raw.eventListenerUuid", "eventName"] satisfies [`${keyof InstrumentationEventDomEventReceived}.${keyof InstrumentationEventDomEventReceived["raw"]}`, keyof DbRecordedEvent],
						// {unique: true}
					)
					eventsStore.createIndex(NameOfIndexForReceivedEventUuidThenEventName,
						["raw.receivedEventUuid", "eventName"] satisfies [`${keyof InstrumentationEventDomEventReceived}.${keyof InstrumentationEventDomEventReceived["raw"]}`, keyof DbRecordedEvent],
						// {unique: true}
					)
					
					const videoStore = db.createObjectStore("videoFragments", {
						keyPath: "videoFragmentUuid" satisfies (keyof DbVideoFragment),
					});
					const mhtmlRecordsStore = db.createObjectStore("mhtmlCaptures", {
						keyPath: "mhtmlCaptureUuid" satisfies keyof DbMhtmlCapture,
					});
					mhtmlRecordsStore.createIndex(NameOfIndexForRecordingUuidThenMhtmlCaptureUuid,
						["recordingUuid", "mhtmlCaptureUuid"] satisfies (keyof DbMhtmlCapture)[]);
				}
				if (oldVersion < 2) {
					if (!db.objectStoreNames.contains('licenses')) {
						const licensesStore = db.createObjectStore("licenses", {
							keyPath: "licenseUuid" satisfies keyof DbSignedLicense,
						});
					}
				}
				if (oldVersion < 3) {
					if (!db.objectStoreNames.contains('harLogs')) {
						const harLogStore = db.createObjectStore("harLogs", {
							keyPath: "recordingUuid" satisfies keyof DbHarLog,
						});
					}
				}
				// if (oldVersion < 4) {
				// 	const txn = db.transaction("events", "versionchange");
				// 	const eventsStore = txn.objectStore("events");
				// 	eventsStore.createIndex(NameOfEventsTableIndexForRecordingUuidThenCategoryThenNameThenDestinationUrlThenNumericIndex,
				// 			["recordingUuid", "category", "eventName", "destination.url", "eventIndex"] satisfies [keyof DbRecordedEvent, keyof DbRecordedEvent, keyof DbRecordedEvent, `${(keyof NetworkEvent) & "destination"}.${keyof NetworkEvent["destination"] & "url"}`, keyof DbRecordedEvent]);
				// }
				if (oldVersion < 4) {
					ProcessInfo.log("Migrating database to version 4");
					const videoFragmentsStore = transaction.objectStore("videoFragments");
					videoFragmentsStore.createIndex(NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode,
							["recordingUuid", "streamId", "timecode"] satisfies [keyof DbVideoFragment, keyof DbVideoFragment, keyof DbVideoFragment]);
				}
				if (oldVersion < 5 && oldVersion >= 1) {
					
					// 1. Access the original object store
					const oldStore = transaction.objectStore("harLogs");

					// 2. Create a temporary object store with your new primary key configuration
					const tempStore = db.createObjectStore("harLogs_temp" as "harLogs", { keyPath: ["recordingUuid", "whenRecordedIso"] as (keyof DbHarLog)[] });

					// 3. Migrate data from the old store to the new store in background
					(async () => {
						const cursor = await oldStore.openCursor();
						if (cursor != null) {
							for await (const item of cursor) {
								const oldHarLog = item.value as Omit<DbHarLog, "whenRecordedIso">;
								const whenRecordedIso = (oldHarLog.entries?.[0]?.startedDateTime as ISODateTimeString | undefined) ?? ISODateTimeString();
								const harLog: DbHarLog = { whenRecordedIso, ...oldHarLog };
								await tempStore.put(harLog);
							}
						}
						
						// 4. Clean up once data migration finishes
						db.deleteObjectStore("harLogs");
						// 5. Rename the temporary store to the original name
						tempStore.name = "harLogs"; 
					})();
				}
			}
		});
		ProcessInfo.log("Opened database");
		return db;
	} catch (e) {
		ProcessInfo.errorLog("Failed to open database", e);
		throw e;
	}
};

let evidenceDatabasePromise: Promise<EvidenceDatabase> | undefined;
export const requireEvidenceDatabase = async () => {
	return await (evidenceDatabasePromise ?? (evidenceDatabasePromise = createDatabase()));
}

export const getDatabase = async () => {
	try {
		return await requireEvidenceDatabase();
	} catch (e) {
		ProcessInfo.errorLog("Failed to create evidence database", e);
		return undefined;
	}
};
